'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { contactInfo } from '@/lib/data';
import { useLocale } from '@/lib/i18n/LocaleContext';

// Erbil sits in UTC+3 (Asia/Baghdad). A live local clock in mono is a small
// "real studio, real place" signal — the kind of human detail templates skip.
function useErbilTime() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const fmt = () =>
      new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Baghdad',
        hour12: false,
      }).format(new Date());
    const tick = () => setTime(fmt());
    // Defer the first set out of the effect body (avoids a synchronous
    // setState-in-effect) and skip any SSR/client time mismatch.
    const raf = requestAnimationFrame(tick);
    const id = setInterval(tick, 15_000);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(id);
    };
  }, []);
  return time;
}

function useFooterLinkGroups() {
  const { dict } = useLocale();
  return {
    studioLinks: [
      { name: dict.footer.studioLinks.home, href: '/' },
      { name: dict.footer.studioLinks.portfolio, href: '/portfolio' },
      { name: dict.footer.studioLinks.about, href: '/about-datacode' },
      { name: dict.footer.studioLinks.articles, href: '/articles' },
      { name: dict.footer.studioLinks.careers, href: '/careers' },
    ],
    serviceLinks: [
      { name: dict.footer.serviceLinks.webDesign, href: '/services/web-design-development' },
      { name: dict.footer.serviceLinks.mobileApp, href: '/services/mobile-app-development' },
      { name: dict.footer.serviceLinks.aiIntegration, href: '/services/ai-integration' },
      { name: dict.footer.serviceLinks.uiUx, href: '/services/ui-ux-design' },
      { name: dict.footer.serviceLinks.allServices, href: '/services' },
    ],
    productLinks: [
      { name: dict.footer.productLinks.dilaCrm, href: '/products/dilacrm' },
      { name: dict.footer.productLinks.dilaErp, href: '/products/dilaerp' },
      { name: dict.footer.productLinks.dilaMenu, href: '/products/dilamenu' },
      { name: dict.footer.productLinks.allProducts, href: '/products' },
    ],
    legalLinks: [
      { name: dict.footer.legalLinks.support, href: '/support' },
      { name: dict.footer.legalLinks.terms, href: '/terms' },
      { name: dict.footer.legalLinks.privacy, href: '/privacy-policy' },
    ],
  };
}

function FooterLink({ href, children, external = false }: {
  href: string;
  children: React.ReactNode;
  external?: boolean;
}) {
  return (
    <Link
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="group relative w-fit text-sm font-light text-white/55 transition-colors duration-300 hover:text-white"
    >
      <span className="relative">
        {children}
        <span className="absolute -bottom-0.5 left-0 h-px w-full origin-right scale-x-0 bg-current transition-transform duration-300 group-hover:origin-left group-hover:scale-x-100" />
      </span>
    </Link>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();
  const erbilTime = useErbilTime();
  const { dict, href } = useLocale();
  const { studioLinks, serviceLinks, productLinks, legalLinks } = useFooterLinkGroups();

  return (
    <footer
      className="self-styled relative overflow-hidden border-t border-white/10 bg-black"
      // Keep the last row clear of the iPhone home indicator (viewport-fit=cover).
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="content-padding">
        {/* ── Top: pitch + link columns ── */}
        <div className="grid grid-cols-1 gap-16 pb-20 pt-24 md:pb-24 md:pt-32 lg:grid-cols-[1.2fr_2fr] lg:gap-24">
          {/* Pitch + primary CTA */}
          <div className="flex flex-col gap-6">
            <span className="eyebrow">{dict.footer.eyebrow}</span>
            <p className="max-w-sm text-2xl font-light leading-snug text-white md:text-3xl">
              {dict.footer.pitchLine1}
              <br />
              <span className="text-white/50">{dict.footer.pitchLine2}</span>
            </p>
            <a
              href={`mailto:${contactInfo.email}`}
              className="group flex w-fit items-center gap-3 text-base font-medium text-white md:text-lg"
            >
              <span className="relative">
                <bdi dir="ltr">{contactInfo.email}</bdi>
                <span className="absolute -bottom-1 left-0 h-px w-full origin-left bg-[var(--color-accent)] transition-transform duration-300 group-hover:scale-x-110" />
              </span>
              <span
                aria-hidden="true"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-black transition-transform duration-500 group-hover:rotate-45"
                style={{ backgroundColor: 'var(--color-accent)' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="7" y1="17" x2="17" y2="7" />
                  <polyline points="7 7 17 7 17 17" />
                </svg>
              </span>
            </a>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-3 md:gap-x-12 lg:grid-cols-4">
            <nav aria-label={dict.footer.columns.studio} className="flex flex-col gap-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                {dict.footer.columns.studio}
              </span>
              {/* inline gap: the un-layered global `nav ul { gap: 40px }`
                  (header nav) would override the Tailwind utility */}
              <ul className="flex flex-col" style={{ gap: '0.625rem' }}>
                {studioLinks.map((link) => (
                  <li key={link.name} className="flex">
                    <FooterLink href={href(link.href)}>{link.name}</FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label={dict.footer.columns.services} className="flex flex-col gap-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                {dict.footer.columns.services}
              </span>
              <ul className="flex flex-col" style={{ gap: '0.625rem' }}>
                {serviceLinks.map((link) => (
                  <li key={link.name} className="flex">
                    <FooterLink href={href(link.href)}>{link.name}</FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label={dict.footer.columns.products} className="flex flex-col gap-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                {dict.footer.columns.products}
              </span>
              <ul className="flex flex-col" style={{ gap: '0.625rem' }}>
                {productLinks.map((link) => (
                  <li key={link.name} className="flex">
                    <FooterLink href={href(link.href)}>{link.name}</FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="col-span-2 flex flex-col gap-5 sm:col-span-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                {dict.footer.columns.contact}
              </span>
              <ul className="flex flex-col gap-2.5">
                <li className="flex">
                  <FooterLink href={`mailto:${contactInfo.email}`}>
                    <bdi dir="ltr">{contactInfo.email}</bdi>
                  </FooterLink>
                </li>
                <li className="flex">
                  <FooterLink href={`tel:${contactInfo.phone.replace(/-/g, '')}`}>
                    <bdi dir="ltr">{contactInfo.phone}</bdi>
                  </FooterLink>
                </li>
                <li>
                  <address className="text-sm font-light not-italic leading-relaxed text-white/55">
                    {dict.footer.address.line1}
                    <br />
                    {dict.footer.address.line2}
                  </address>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="flex flex-col gap-6 border-t border-white/[0.07] py-9 md:flex-row md:items-center md:justify-between md:py-10">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="text-[13px] font-light text-white/55">
              &copy; {currentYear} {dict.footer.copyright}
            </span>
            {legalLinks.map((link) => (
              <Link
                key={link.name}
                href={href(link.href)}
                className="text-[13px] font-light text-white/55 transition-colors hover:text-white"
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {/* Socials */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {contactInfo.socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[12px] text-white/55 transition-colors hover:text-white"
                >
                  {social.name}
                </a>
              ))}
            </div>

            {/* Live studio clock */}
            <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-white/55">
              <span
                aria-hidden="true"
                className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
                style={{ backgroundColor: 'var(--color-accent)' }}
              />
              {dict.footer.erbilLabel} {erbilTime || '--:--'}
            </span>

            {/* Back to top */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="group flex cursor-pointer items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.24em] text-white/55 transition-colors hover:text-white"
            >
              {dict.footer.backToTop}
              <svg
                className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Ghost wordmark — bleeds off the bottom edge */}
      <div aria-hidden="true" className="pointer-events-none select-none">
        <div
          className="font-six-caps keep-latin -mb-[5vw] mt-8 text-center text-[17.5vw] leading-[0.72] text-transparent md:mt-12"
          style={{ WebkitTextStroke: '1px rgba(255,255,255,0.06)' }}
        >
          DATACODE
        </div>
      </div>
    </footer>
  );
}
